import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';

const PLAN_INLINE_SUMMARIES_QUERY = `
  query PlanInlineSummaries($projectId: ID!, $planIds: [ID!]!) {
    planInlineSummaries(projectId: $projectId, planIds: $planIds) {
      planId
      reports {
        id
        projectId
        planId
        title
        status
        qualityScore
        reviewRecommendation
        reviewStatus
        createdByName
        createdAt
      }
      postMortems {
        id
        projectId
        planId
        title
        status
        createdByName
        createdAt
      }
      codeReviews {
        id
        projectId
        sourcePlanId
        title
        status
        createdByName
        findingCount
        p0Count
        p1Count
        p2Count
        p3Count
        createdAt
      }
    }
  }
`;

export type PlanInlineCompletionReport = {
  id: string;
  projectId: string;
  planId: string | null;
  title: string;
  status: string;
  qualityScore: number | null;
  reviewRecommendation: string | null;
  reviewStatus: string | null;
  createdByName: string | null;
  createdAt: string;
};

export type PlanInlinePostMortem = {
  id: string;
  projectId: string;
  planId: string | null;
  title: string;
  status: string;
  createdByName: string | null;
  createdAt: string;
};

export type PlanInlineCodeReview = {
  id: string;
  projectId: string;
  sourcePlanId: string | null;
  title: string;
  status: string;
  createdByName: string | null;
  findingCount: number;
  p0Count: number;
  p1Count: number;
  p2Count: number;
  p3Count: number;
  createdAt: string;
};

export type PlanInlineSummaryData = {
  planId: string;
  reports: PlanInlineCompletionReport[];
  postMortems: PlanInlinePostMortem[];
  codeReviews: PlanInlineCodeReview[];
};

type PlanInlineSummaryPayload = {
  data?: { planInlineSummaries: PlanInlineSummaryData[] } | null;
  errors?: Array<{ message: string }>;
};

export const fetchPlanInlineSummariesGraphql = async (
  token: string,
  projectId: string,
  planIds: string[],
): Promise<PlanInlineSummaryData[]> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/graphql`, {
    method: 'POST',
    cache: 'no-store',
    headers: authHeaders(token, true),
    body: JSON.stringify({
      query: PLAN_INLINE_SUMMARIES_QUERY,
      variables: { projectId, planIds },
    }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch plan inline summaries: ${response.status}`);
  }

  const payload = (await response.json()) as PlanInlineSummaryPayload;
  if (payload.errors && payload.errors.length > 0) {
    throw new Error(payload.errors[0]?.message ?? 'Plan inline summaries GraphQL failed');
  }

  return payload.data?.planInlineSummaries ?? [];
};
